import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, ImageSourcePropType, Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View, useWindowDimensions } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { ArrowIcon, Button as Button95, CloseIcon, Hourglass, NumberInput, Panel, Progress, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import { CustomDivider } from "@/components/CustomDivider";
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { AppContext } from '@/components/AppContext';
import { Community } from "@/types/community";
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { SortableGridRenderItem, SortableGridDragEndCallback } from 'react-native-sortables';
import Sortable from 'react-native-sortables';
import { Image } from 'expo-image';
import * as ImagePicker from 'expo-image-picker';
import {launchCamera, launchImageLibrary, Asset} from 'react-native-image-picker';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import {
  startUpload,
  removeUpload,
  updateVisuals,
  Visual,
} from '@/state/uploads';
import { Buffer } from 'buffer';
import CancelCreate from '@/components/CancelCreate';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    gap: 20,
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
  },
  numeric: {
    width: 80,
  },
  inputWrapper: {
    gap: 10,
    alignItems: 'center',
  },
  card: {
    height: 300,
    //borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    //borderWidth: 1,
  },
  center: {
    alignItems: 'center',
    gap: 20,
    paddingTop: 20,
  },
  image: {
    width: '100%',
  },
  deleteButton: {
    position: 'absolute',
    top: 10,
    right: 10,
  },
  deleteButtonPanel: {
    padding: 10,
  },
  uploadButtons: {
    flexDirection: 'row',
    gap: 20,
  },
  progress: {
    width: '75%',
  },
});

export default function RabblVisuals() {
  const {height, width} = useWindowDimensions();
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const router = useRouter();
  const items = useSelector((state: RootState) => state.uploads.visuals);
  const dispatch = useDispatch();
  const community = useSelector((state: RootState) => state.user.creating_community);
  const userInfo = useSelector((state: RootState) => state.user.user_info);
  const { id } = useLocalSearchParams<{ id?: string }>();

  useEffect(() => {
    if (community.main_visual > 0) {
      const visuals: Visual[] = [
        {
          id: '' + community.main_community_file,
          image: {
            uri: AppUtils.createBackendLink('/rabbl/api/communityvisual?thumbnail=yes&community_id=' + community.id + '&id=' + community.main_visual),
          },
          status: 'ready',
          progress: {
            uploaded: -1,
            total: -1,
          }
        }
      ];
      dispatch(updateVisuals(visuals));
    }
  }, [community]);

  const onSubmit = useCallback(async () => {
    try {
      showSpinner();

      if (AppUtilities.DEBUGLEVEL.uploads) {
        console.log("onSubmit:");
        console.dir(items);
      }

      const response = await AppUtilities.fetchAuthorized(
        "RabblVisuals.onSubmit",
        "/rabbl/api/set_rabbl_visuals",
        "POST",
        {
          visuals: items,
          id: id,
        }
      );
      const creating_community: Community = ((response as any).body as any).creating_community;
      dispatch(setData(response));
      // Don't reset visuals here because the user might click back. TODO: reset when creating a new rabbl
      //dispatch(updateVisuals([]));
      if (id && id.length > 0) {
        if (router.canGoBack()) {
          router.back();
        } else {
          router.navigate("/rabbls");
        }
      } else {
        router.navigate(AppUtils.getRabblCreateNextPageLink(creating_community, userInfo) as RelativePathString);
      }

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(), true);
      AppUtilities.handleException(e, "RabblVisuals.onSubmit");
    } finally {
      hideSpinner();
    }
  }, [items]);

  const pickImage = useCallback(async () => {
    try {
      // No permissions request is necessary for launching the image library.
      // Manually request permissions for videos on iOS when `allowsEditing` is set to `false`
      // and `videoExportPreset` is `'Passthrough'` (the default), ideally before launching the picker
      // so the app users aren't surprised by a system dialog after picking a video.
      // See "Invoke permissions for videos" sub section for more details.
      const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();

      if (!permissionResult.granted) {
        Alert.alert('Permission required', 'Permission to access the media library is required.');
        return;
      }

      AppUtilities.info("RabblVisuals.pickImage community:");
      AppUtilities.dir(community);

      // https://docs.expo.dev/versions/latest/sdk/imagepicker/#imagepickeroptions
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        allowsEditing: true,
        quality: 1,
        exif: false,
      });

      if (AppUtilities.DEBUGLEVEL.uploads) {
        console.log(result);
      }

      if (!result.canceled) {
        result.assets.forEach((asset) => {
          const visual : Visual = {
            id: AppUtils.getUUID(),
            image: asset,
            status: 'uploading',
            progress: {
              uploaded: -1,
              total: -1,
            }
          };
          dispatch(startUpload(visual));
          upload(visual, asset, undefined);
        });
      }
    } catch (e) {
      // Might just be a cancel?
      //AppUtils.showErrorToast(AppUtilities.getErrors(e), () => pickImage(), true);

      AppUtilities.handleException(e, "RabblVisuals.pickImage");
    }
  }, [items, community]);

  const takePicture = useCallback(async () => {
    try {
      const result = await launchCamera({
        mediaType: 'photo',
      });

      if (AppUtilities.DEBUGLEVEL.uploads) {
        console.log(result);
      }
      if (!result.didCancel && result.assets) {
        result.assets.forEach((asset) => {
          const visual : Visual = {
            id: AppUtils.getUUID(),
            image: asset,
            status: 'uploading',
            progress: {
              uploaded: -1,
              total: -1,
            }
          };
          dispatch(startUpload(visual));
          upload(visual, undefined, asset);
        });
      }
    } catch (e) {
      // Might just be a cancel?
      // AppUtils.showErrorToast(AppUtilities.getErrors(e), () => takePicture(), true);
      AppUtilities.handleException(e, "RabblVisuals.takePicture");
    }
  }, [items, community]);
  
  const upload = useCallback(async (visual: Visual, asset1?: ImagePicker.ImagePickerAsset, asset2?: Asset) => {
    await AppUtils.handleVisualUpload(
      "CommunityFile",
      community.identity_id,
      dispatch,
      visual,
      asset1,
      asset2,
    );
  }, [community]);
  
  const renderItem = useCallback(
    (item: Visual) => (
      <View key={'' + item.id} style={styles.card}>
        <Image
          source={item.image}
          contentFit="contain"
          style={[
            styles.image,
            {
              height: Math.max(Math.min(300, Math.floor(height / 2)), 100),
            },
          ]}
          pointerEvents="none"
        />
        <Pressable
          onPress={() => {
            dispatch(removeUpload(item));
          }}
          style={styles.deleteButton}
        >
          <Panel style={styles.deleteButtonPanel}>
            <CloseIcon />
          </Panel>
        </Pressable>
      </View>
    ),
    []
  );

  const dragFinished = useCallback<SortableGridDragEndCallback<Visual>>(
    ({ data }) => {
      dispatch(updateVisuals(data));
    },
    [items]
  );

  let isUploading = false;
  let sumUploaded = 0;
  let sumTotal = 0;
  items.forEach((v) => {
    if (v.status == 'uploading') {
      isUploading = true;
      if (v.progress.uploaded != -1) {
        sumUploaded += v.progress.uploaded;
      }
      if (v.progress.total != -1) {
        sumTotal += v.progress.total;
      }
    }
  });
  let progressPercent = 0;
  if (sumTotal > 0) {
    progressPercent = Math.round((sumUploaded / sumTotal) * 100);
  }
  if (AppUtilities.DEBUGLEVEL.uploads) {
    console.log("Calculated progress = " + progressPercent + " from " + sumUploaded + "," + sumTotal);
  }

  return (
    <BasicScrollView center={false} useRefreshComponent={false}>
      <Meta title={"Rabbl visual"} />

      <View style={styles.center}>
        <AppText>Upload a picture representing your Rabbl. Square image preferred.</AppText>
      </View>

      <View style={styles.inputWrapper}>
        <View style={styles.uploadButtons}>
          <Button95 onPress={pickImage}>Upload Picture</Button95>
          {
            false && AppUtilities.isWeb() && <Button95 onPress={takePicture}>Take Picture</Button95>
          }
        </View>
      </View>

      {
        items.length > 0 && (
          renderItem(items[items.length - 1])
        )
      }
      {/*
      <Sortable.Grid
        columns={AppUtils.isSmallScreen(width) ? 2 : 3}
        data={items}
        renderItem={renderItem}
        rowGap={20}
        columnGap={20}
        dragActivationDelay={100}
        showDropIndicator={true}
        onDragEnd={dragFinished}
      />
      */}

      <View style={styles.inputWrapper}>
        { isUploading && (
          <>
            <CustomText>Uploading...</CustomText>
            <Progress variant='default' percent={progressPercent} style={styles.progress} />
            <CustomDivider fullWidth={true} />
          </>
        ) }
        <Button95 onPress={onSubmit} disabled={items.length == 0 || isUploading}>Next</Button95>
      </View>

      { (id == null || id.length == 0) && <CancelCreate /> }
    
      <Footer />
    </BasicScrollView>
  );
}
